// ─── Reply markup: the small grammar a support reply is written in ──────────
//
// A rep types a ticket reply in the CRM. The same text is then shown in three
// places that cannot share code: the customer's dashboard thread (this repo),
// the case-reply email (squish-backend, lib/reply-markup.js), and the CRM's own
// case feed (squish-crm-frontend, src/lib/replyMarkup.js). Salesforce stores
// the text as-is in CaseComment.CommentBody — plain text, 4000 characters —
// so whatever the rep can do has to survive as readable plain text and be
// re-rendered identically by all three.
//
// This file is the parser, and it is COPIED VERBATIM into the other two repos
// (CommonJS export line aside). Change it in one, change it in all three, and
// run each repo's replyMarkup fixture test — the fixtures are the same set on
// purpose, so drift shows up as a failing test rather than as an email that
// renders a link the dashboard does not.
//
// THE GRAMMAR, deliberately small (a subset of Markdown, so a rep who knows
// Markdown is never surprised, and so the CRM can offer buttons for it):
//
//   [text](https://url)   a link — only http(s) and mailto hrefs; anything else
//                         is left as the literal text, brackets and all
//   https://…  a@b.co     bare URLs and addresses become links on their own
//   **bold**  _italic_  *italic*  `code`
//   - item / * item       a bullet list (one item per line)
//   1. item               a numbered list
//   blank line            a new paragraph; a single newline is a line break
//
// Nothing else. No headings, no images, no HTML — a reply is data, and the
// renderer on each side escapes every character of it. Inline formatting can
// be switched off (`inlineMarkup: false`) for text a CUSTOMER wrote: their
// words are shown exactly as typed, with only bare URLs made clickable, so a
// customer cannot dress a link up as something it is not in front of a rep.
//
// Output is a plain array of blocks:
//   { type: 'paragraph', lines: Inline[][] }       one Inline[] per line
//   { type: 'list', ordered: boolean, items: Inline[][] }
// and an Inline is one of:
//   { type: 'text', value }   { type: 'code', value }
//   { type: 'link', href, children: Inline[] }
//   { type: 'strong', children: Inline[] }   { type: 'em', children: Inline[] }

// A bare URL. Stops at whitespace, quotes, angle brackets and backticks (no
// real URL contains them, and a pasted `<a href="…">` must not swallow its own
// closing tag), and leaves trailing punctuation outside the link — the full
// stop after a URL at the end of a sentence is prose, not address.
const URL_RE = /^https?:\/\/[^\s<>"'`]*[^\s<>"'`.,;:!?)\]&]/;
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}(?![A-Z0-9])/i;
// [text](href) — the href is allowlisted by scheme HERE, at parse time, so a
// `javascript:` or `data:` link never even becomes a link node.
const LINK_RE = /^\[([^\]\n]{1,300})\]\(((?:https?:\/\/|mailto:)[^\s()<>"'`]+)\)/;
const CODE_RE = /^`([^`\n]+)`/;
const STRONG_RE = /^\*\*(?=\S)([^\n]*?\S)\*\*/;
const EM_UNDERSCORE_RE = /^_(?=\S)([^_\n]*?\S)_(?![A-Za-z0-9_])/;
const EM_STAR_RE = /^\*(?=[^*\s])([^*\n]*?[^*\s])\*(?!\*)/;

const BULLET_RE = /^\s{0,3}[-*•]\s+(.*)$/;
const ORDERED_RE = /^\s{0,3}\d{1,3}[.)]\s+(.*)$/;

const isWordChar = (ch) => /[A-Za-z0-9_]/.test(ch || '');

function pushText(out, value) {
  if (!value) return;
  const last = out[out.length - 1];
  if (last && last.type === 'text') last.value += value;
  else out.push({ type: 'text', value });
}

/**
 * Inline pass over one line.
 * @param {string} line
 * @param {{inlineMarkup:boolean, depth?:number}} opts
 * @returns {Inline[]}
 */
function parseInline(line, opts) {
  const out = [];
  const markup = opts.inlineMarkup !== false;
  const depth = opts.depth || 0;
  let i = 0;
  let text = '';
  const flush = () => { pushText(out, text); text = ''; };

  while (i < line.length) {
    const rest = line.slice(i);
    const prev = i === 0 ? '' : line[i - 1];
    let m;

    if (markup && depth < 3) {
      if ((m = CODE_RE.exec(rest))) {
        flush(); out.push({ type: 'code', value: m[1] }); i += m[0].length; continue;
      }
      if (rest[0] === '[' && (m = LINK_RE.exec(rest))) {
        flush();
        out.push({ type: 'link', href: m[2], children: [{ type: 'text', value: m[1] }] });
        i += m[0].length; continue;
      }
      if (rest.startsWith('**') && (m = STRONG_RE.exec(rest))) {
        flush();
        out.push({ type: 'strong', children: parseInline(m[1], { ...opts, depth: depth + 1 }) });
        i += m[0].length; continue;
      }
      if (rest[0] === '_' && !isWordChar(prev) && (m = EM_UNDERSCORE_RE.exec(rest))) {
        flush();
        out.push({ type: 'em', children: parseInline(m[1], { ...opts, depth: depth + 1 }) });
        i += m[0].length; continue;
      }
      if (rest[0] === '*' && !isWordChar(prev) && (m = EM_STAR_RE.exec(rest))) {
        flush();
        out.push({ type: 'em', children: parseInline(m[1], { ...opts, depth: depth + 1 }) });
        i += m[0].length; continue;
      }
    }

    // Autolinks run in BOTH modes — a bare URL the customer pasted should be a
    // link for the rep too. Word-boundary on the left so "xhttps://" or the
    // tail of an address is not re-linked from the middle.
    if (!isWordChar(prev) && prev !== '/' && prev !== '@' && prev !== '.') {
      if (rest[0] === 'h' && (m = URL_RE.exec(rest))) {
        flush(); out.push({ type: 'link', href: m[0], children: [{ type: 'text', value: m[0] }] });
        i += m[0].length; continue;
      }
      if ((m = EMAIL_RE.exec(rest))) {
        flush(); out.push({ type: 'link', href: `mailto:${m[0]}`, children: [{ type: 'text', value: m[0] }] });
        i += m[0].length; continue;
      }
    }

    text += line[i];
    i += 1;
  }
  flush();
  return out;
}

/**
 * @param {string} text  what the rep (or customer) wrote
 * @param {{inlineMarkup?: boolean}} [opts]  false → autolinks only, no formatting, no lists
 * @returns {Block[]}
 */
function parseReplyMarkup(text, opts = {}) {
  const inlineMarkup = opts.inlineMarkup !== false;
  const normalised = String(text ?? '').replace(/\r\n?/g, '\n').trim();
  if (!normalised) return [];

  const blocks = [];
  for (const chunk of normalised.split(/\n{2,}/)) {
    const lines = chunk.split('\n');
    let para = null;
    let list = null;
    const closePara = () => { if (para) { blocks.push(para); para = null; } };
    const closeList = () => { if (list) { blocks.push(list); list = null; } };

    for (const raw of lines) {
      const bullet = inlineMarkup ? BULLET_RE.exec(raw) : null;
      const ordered = inlineMarkup && !bullet ? ORDERED_RE.exec(raw) : null;
      if (bullet || ordered) {
        closePara();
        const isOrdered = Boolean(ordered);
        if (!list || list.ordered !== isOrdered) { closeList(); list = { type: 'list', ordered: isOrdered, items: [] }; }
        list.items.push(parseInline((bullet || ordered)[1], { inlineMarkup }));
        continue;
      }
      closeList();
      if (!para) para = { type: 'paragraph', lines: [] };
      para.lines.push(parseInline(raw, { inlineMarkup }));
    }
    closePara();
    closeList();
  }
  return blocks;
}

/** The text with every mark stripped — for previews, subjects, notifications. */
function inlineToPlain(nodes) {
  return nodes.map((n) => (n.type === 'text' || n.type === 'code') ? n.value : inlineToPlain(n.children || [])).join('');
}

function blocksToPlain(blocks) {
  return blocks.map((b) => (
    b.type === 'list'
      ? b.items.map((it, i) => `${b.ordered ? `${i + 1}.` : '•'} ${inlineToPlain(it)}`).join('\n')
      : b.lines.map(inlineToPlain).join('\n')
  )).join('\n\n');
}

export { parseReplyMarkup, parseInline, inlineToPlain, blocksToPlain, URL_RE, EMAIL_RE, LINK_RE };
